/**
 * site_contacts + site_contact_assignments + operator_notification_settings
 * (DEC-146, Phase 17 urgent slice — migration 0072).
 *
 * The MINIMAL operator-contact model needed to route an immediate urgent
 * "load complete" email:
 *   - site_contacts: external recipients (pumper, landman, …) under a tenant's
 *     operator-customer (hauler_operators). Doubles as Phase 15 portal access.
 *   - site_contact_assignments: many-to-many contact ↔ well / hauler_location.
 *     (DEC-147 reconciliation: tank_battery_id FKs hauler_locations.)
 *   - operator_notification_settings: the is_enabled gate — an explicit
 *     is_enabled=false suppresses ALL email for that operator, even urgent.
 *
 * The full DEC-146 cadence/digest machinery (scheduled summaries, cadence
 * windows, disposal_operator_contacts, settings UI) is OUT of this slice.
 */

import {
  boolean,
  check,
  foreignKey,
  index,
  pgTable,
  text,
  timestamp,
  unique,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { tenants } from './users.js';
import { haulerOperators, haulerLocations, wells } from './platform-catalog.js';
import { importBatches } from './imports.js';

export const siteContacts = pgTable(
  'site_contacts',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // DEC-146 §4 — hauler_operators (the tenant's operator-customer), NOT the
    // legacy operators table and NOT platform_operators directly.
    operatorId: uuid('operator_id')
      .notNull()
      .references(() => haulerOperators.id, { onDelete: 'cascade' }),
    name: text('name').notNull(),
    email: text('email').notNull(),
    phone: text('phone'),
    role: text('role'),
    isActive: boolean('is_active').notNull().default(true),
    portalAccess: boolean('portal_access').notNull().default(false),
    /**
     * Legacy 17.0 column. A plain UUID is not a credential, and nothing reads it since
     * 17.1 (DEC-324): unsubscribe links carry an HMAC-signed token over the contact id
     * (services/operator-email-unsubscribe-token.ts). Kept until a contract migration.
     */
    unsubscribeToken: uuid('unsubscribe_token')
      .notNull()
      .default(sql`gen_random_uuid()`),
    /**
     * 17.1 / DEC-324 D-09 — set when the CONTACT unsubscribes through their email link
     * (migration 0130). The hauler can see it but no hauler route may clear it: only the
     * contact can re-subscribe, from the same page.
     */
    unsubscribedAt: timestamp('unsubscribed_at', { withTimezone: true }),
    /**
     * 17.6 / DEC-324 D-11 (migration 0152) — 'OK' | 'BOUNCING' | 'COMPLAINED', set from SES
     * failure events; every sender skips a non-OK address.
     */
    emailStatus: text('email_status').notNull().default('OK'),
    emailStatusAt: timestamp('email_status_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('site_contacts_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'site_contacts_import_batch_tenant_fk',
    }).onDelete('set null'),
    index('site_contacts_tenant_operator_idx').on(t.tenantId, t.operatorId),
  ],
);

export const siteContactAssignments = pgTable(
  'site_contact_assignments',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    contactId: uuid('contact_id')
      .notNull()
      .references(() => siteContacts.id, { onDelete: 'cascade' }),
    assignmentType: text('assignment_type').notNull(),
    wellId: uuid('well_id').references(() => wells.id, { onDelete: 'cascade' }),
    // DEC-147 — DEC-146's tank_battery_id is a hauler_locations row.
    tankBatteryId: uuid('tank_battery_id').references(() => haulerLocations.id, {
      onDelete: 'cascade',
    }),
    receiveNotifications: boolean('receive_notifications').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('site_contact_assignments_contact_idx').on(t.contactId),
    index('site_contact_assignments_well_idx').on(t.wellId),
    index('site_contact_assignments_battery_idx').on(t.tankBatteryId),
    check('site_contact_assignments_type_check', sql`assignment_type IN ('WELL', 'TANK_BATTERY')`),
    check(
      'site_contact_assignments_target_xor',
      sql`(assignment_type = 'WELL' AND well_id IS NOT NULL AND tank_battery_id IS NULL)
        OR (assignment_type = 'TANK_BATTERY' AND tank_battery_id IS NOT NULL AND well_id IS NULL)`,
    ),
  ],
);

export const operatorNotificationSettings = pgTable(
  'operator_notification_settings',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    operatorId: uuid('operator_id')
      .notNull()
      .references(() => haulerOperators.id, { onDelete: 'cascade' }),
    /**
     * Legacy (0072). Superseded by the D-12 pair below in 17.2 (migration 0147) and no
     * longer read; kept until a contract migration because the previous release reads
     * them during a rolling deploy.
     */
    cadence: text('cadence').notNull().default('ON_COMPLETION'),
    isEnabled: boolean('is_enabled').notNull().default(false),
    /**
     * DEC-324 D-12 — two INDEPENDENT settings. NULL inherits the tenant default
     * (hauler_tenant_settings.default_each_haul_enabled / default_summary_cadence),
     * which ships OFF (D-03).
     */
    eachHaulEnabled: boolean('each_haul_enabled'),
    /** 'OFF' | 'TWO_HOURS' | 'SIX_HOURS' | 'TWELVE_HOURS' | 'DAILY', or NULL = inherit. */
    summaryCadence: text('summary_cadence'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('operator_notification_settings_tenant_idx').on(t.tenantId),
    unique('operator_notification_settings_operator_unique').on(t.operatorId),
    check(
      'operator_notification_settings_cadence_check',
      sql`cadence IN ('TWO_HOURS','SIX_HOURS','TWELVE_HOURS','TWENTY_FOUR_HOURS','ON_COMPLETION','DISABLED')`,
    ),
  ],
);

export type SiteContact = typeof siteContacts.$inferSelect;
export type NewSiteContact = typeof siteContacts.$inferInsert;
export type SiteContactAssignment = typeof siteContactAssignments.$inferSelect;
export type OperatorNotificationSettings = typeof operatorNotificationSettings.$inferSelect;
